import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
  Chip,
  useMediaQuery,
} from "@mui/material";
import { Delete, Visibility, GetApp, SwapHoriz } from "@mui/icons-material";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import CommonTable1 from "app/components/Tables/CommonTable1";
import CustomLoader from "app/components/common/CustomLoader";
import {
  getAllSubscriptions,
  getAllPlans,
  getProduct,
} from "app/services/org/subscription.service";
import useSelectorHook from "app/services/redux/useSelectorHook";
import TableActionDropdown from "app/components/common/tableComponents/TableActionDropdown";
import CommonDialog from "app/components/common/CommonDialog";
import ManageSubscriptionDialog from "../../components/ManageSubscriptionDialog";

const Subscriptions = () => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { selectedSession } = useSelectorHook();

  const [state, setState] = useState({
    manageDialog: false,
    viewDialog: false,
    filterParams: {},
    page: 1,
    selectedSubscription: null,
    viewData: null,
    actionType: "change",
  });

  const [productList, setProductList] = useState([]);
  const [planList, setPlanList] = useState([]);

  // Fetch Products
  const { isLoading: productsLoading } = useQuery(
    "getProductForSubscriptionDropdown",
    () => getProduct({ session: selectedSession }),
    {
      onSuccess: (res) => {
        setProductList(res?.data?.data || []);
      },
      onError: (err) => {
        toast.error("Failed to load products");
      },
    },
  );

  // Fetch Plans
  const { isLoading: plansLoading } = useQuery(
    ["getAllPlansForSubscription", selectedSession],
    () =>
      getAllPlans({
        page: 1,
        limit: 1000,
        session: selectedSession,
      }),
    {
      onSuccess: (res) => {
        setPlanList(res?.data?.data || []);
      },
      onError: (err) => {
        toast.error("Failed to load plans");
      },
    },
  );

  // Fetch Subscriptions
  const {
    isLoading: subscriptionsLoading,
    isFetching: subscriptionsFetching,
    data: subscriptionsData,
    refetch: refetchSubscriptions,
  } = useQuery(
    ["getAllSubscriptions", state.filterParams, state.page, selectedSession],
    () =>
      getAllSubscriptions({
        ...state.filterParams,
        page: state.page,
        limit: 10,
        session: selectedSession,
      }),
    {
      onError: (err) => {
        toast.error(
          err?.response?.data?.message || "Failed to load subscriptions",
        );
      },
    },
  );

  const handleOpenManageDialog = (subscription, action) => {
    setState((prev) => ({
      ...prev,
      manageDialog: true,
      selectedSubscription: subscription,
      actionType: action,
    }));
  };

  const handleCloseManageDialog = () => {
    setState((prev) => ({
      ...prev,
      manageDialog: false,
      selectedSubscription: null,
    }));
  };

  const handleOpenViewDialog = (data) => {
    setState((prev) => ({
      ...prev,
      viewDialog: true,
      viewData: data,
    }));
  };

  const handleCloseViewDialog = () => {
    setState((prev) => ({
      ...prev,
      viewDialog: false,
      viewData: null,
    }));
  };

  const handlePageChange = (newPage) => {
    setState((prev) => ({
      ...prev,
      page: newPage + 1,
    }));
  };

  // Get product name by ID
  const getProductName = (productId) => {
    const product = productList.find((p) => p.id === productId);
    return product?.productName || "N/A";
  };

  // Get plan name by ID
  const getPlanName = (planId) => {
    const plan = planList.find((p) => p.id === planId);
    return plan?.planName || "N/A";
  };

  // Get status badge color
  const getStatusColor = (status) => {
    const statusColors = {
      active: "success",
      inactive: "default",
      expired: "error",
      cancelled: "error",
      suspended: "warning",
    };
    return statusColors[status] || "default";
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: "customerId",
        header: "Customer ID",
        size: 150,
        Cell: ({ row }) => (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {row.original.customerId?.substring(0, 8)}...
          </Typography>
        ),
      },
      {
        accessorKey: "planId",
        header: "Plan",
        size: 150,
        Cell: ({ row }) => (
          <Typography variant="body2">
            {getPlanName(row.original.planId)}
          </Typography>
        ),
      },
      {
        accessorKey: "productId",
        header: "Product",
        size: 120,
        Cell: ({ row }) => (
          <Typography variant="body2">
            {getProductName(row.original.productId)}
          </Typography>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 110,
        Cell: ({ row }) => (
          <Chip
            label={
              row.original.status?.charAt(0).toUpperCase() +
              row.original.status?.slice(1)
            }
            size="small"
            color={getStatusColor(row.original.status)}
            variant="outlined"
          />
        ),
      },
      {
        accessorKey: "startDate",
        header: "Start Date",
        size: 120,
        Cell: ({ row }) => (
          <Typography variant="body2">
            {row.original.startDate
              ? new Date(row.original.startDate).toLocaleDateString()
              : "N/A"}
          </Typography>
        ),
      },
      {
        accessorKey: "endDate",
        header: "End Date",
        size: 120,
        Cell: ({ row }) => (
          <Typography variant="body2">
            {row.original.endDate
              ? new Date(row.original.endDate).toLocaleDateString()
              : "N/A"}
          </Typography>
        ),
      },
      {
        accessorKey: "billingCycle",
        header: "Billing",
        size: 100,
        Cell: ({ row }) => (
          <Chip
            label={
              row.original.billingCycle?.charAt(0).toUpperCase() +
              row.original.billingCycle?.slice(1)
            }
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        id: "actions",
        header: "Actions",
        size: 120,
        Cell: ({ row }) => (
          <TableActionDropdown>
            <Box
              onClick={() => handleOpenViewDialog(row.original)}
              sx={{
                p: 1,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 1,
                "&:hover": { backgroundColor: "#f6f8fc" },
              }}
            >
              <Visibility fontSize="small" />
              View
            </Box>
            {row.original.status === "active" && (
              <>
                <Box
                  onClick={() =>
                    handleOpenManageDialog(row.original, "upgrade")
                  }
                  sx={{
                    p: 1,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    "&:hover": { backgroundColor: "#f6f8fc" },
                  }}
                >
                  <SwapHoriz fontSize="small" />
                  Change
                </Box>
                <Box
                  onClick={() => handleOpenManageDialog(row.original, "cancel")}
                  sx={{
                    p: 1,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "error.main",
                    "&:hover": { backgroundColor: "#f6f8fc" },
                  }}
                >
                  <Delete fontSize="small" />
                  Cancel
                </Box>
              </>
            )}
            {row.original.status === "expired" && (
              <Box
                onClick={() => handleOpenManageDialog(row.original, "renew")}
                sx={{
                  p: 1,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  "&:hover": { backgroundColor: "#f6f8fc" },
                }}
              >
                <GetApp fontSize="small" />
                Renew
              </Box>
            )}
          </TableActionDropdown>
        ),
      },
    ],
    [productList, planList],
  );

  const tableData = useMemo(
    () => subscriptionsData?.data?.data || [],
    [subscriptionsData],
  );

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Helmet>
        <title>Subscriptions Management | Ekalsutra</title>
      </Helmet>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Customer Subscriptions
        </Typography>
      </Box>

      {/* Table */}
      {subscriptionsLoading ? (
        <CustomLoader />
      ) : (
        <CommonTable1
          columns={columns}
          data={tableData}
          enablePagination={true}
          pageCount={Math.ceil((subscriptionsData?.data?.total || 0) / 10)}
          state={{
            pagination: {
              pageIndex: state.page - 1,
              pageSize: 10,
            },
          }}
          onPaginationChange={(updater) => {
            const newPaginationState = updater();
            handlePageChange(newPaginationState.pageIndex);
          }}
        />
      )}

      {/* Manage Subscription Dialog */}
      <ManageSubscriptionDialog
        open={state.manageDialog}
        onClose={handleCloseManageDialog}
        refetch={refetchSubscriptions}
        subscriptionData={state.selectedSubscription}
        planList={planList}
        actionType={state.actionType}
      />

      {/* View Subscription Dialog */}
      <CommonDialog
        open={state.viewDialog}
        onClose={handleCloseViewDialog}
        title="Subscription Details"
        maxWidth="sm"
      >
        {state.viewData && (
          <Box sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ color: "gray" }}>
                    Customer ID
                  </Typography>
                  <Typography variant="body2">
                    {state.viewData.customerId?.substring(0, 12)}...
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: "gray" }}>
                    Status
                  </Typography>
                  <Chip
                    label={
                      state.viewData.status?.charAt(0).toUpperCase() +
                      state.viewData.status?.slice(1)
                    }
                    size="small"
                    color={getStatusColor(state.viewData.status)}
                  />
                </Box>
              </Box>

              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ color: "gray" }}>
                    Plan
                  </Typography>
                  <Typography variant="body2">
                    {getPlanName(state.viewData.planId)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: "gray" }}>
                    Product
                  </Typography>
                  <Typography variant="body2">
                    {getProductName(state.viewData.productId)}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ color: "gray" }}>
                    Start Date
                  </Typography>
                  <Typography variant="body2">
                    {state.viewData.startDate
                      ? new Date(state.viewData.startDate).toLocaleDateString()
                      : "N/A"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: "gray" }}>
                    End Date
                  </Typography>
                  <Typography variant="body2">
                    {state.viewData.endDate
                      ? new Date(state.viewData.endDate).toLocaleDateString()
                      : "N/A"}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ color: "gray" }}>
                  Billing Cycle
                </Typography>
                <Chip
                  label={
                    state.viewData.billingCycle?.charAt(0).toUpperCase() +
                    state.viewData.billingCycle?.slice(1)
                  }
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Stack>
          </Box>
        )}
      </CommonDialog>
    </Container>
  );
};

export default Subscriptions;
